import { AiRequest, AiResponse } from "./protocol"

export class AiBridge {
  private pending = new Map<
    string,
    {
      resolve: (value: string) => void
      reject: (reason?: any) => void
      timeout: NodeJS.Timeout
    }
  >()
  private destroyed = false

  private static generateId(): string {
    // `crypto.randomUUID()` is not supported in some environments.
    // Fallback: generate a sufficiently unique short id.
    const maybeCrypto = typeof crypto !== 'undefined' ? crypto : undefined
    if (maybeCrypto && typeof (maybeCrypto as any).randomUUID === 'function') {
      return (maybeCrypto as any).randomUUID()
    }

    const base = Math.random().toString(36).slice(2) + Date.now().toString(36)
    return base.slice(0, 20)
  }

  constructor() {
    window.addEventListener("message", this.handleMessage)
  }

  destroy() {
    this.destroyed = true
    window.removeEventListener("message", this.handleMessage)

    // Reject all in-flight requests to avoid hanging promises and leaked timeouts.
    for (const [requestId, pending] of this.pending.entries()) {
      clearTimeout(pending.timeout)
      try {
        pending.reject("AI bridge destroyed")
      } catch {
        // ignore
      }
      this.pending.delete(requestId)
    }
  }

  send(
    action: AiRequest["action"],
    userMessage: string,
    context: AiRequest["context"],
    utility: AiRequest["utility"],
    timeoutMs: number = 3 * 60 * 1000,
  ) {
    if (this.destroyed) {
      return Promise.reject("AI bridge destroyed")
    }

    const requestId = AiBridge.generateId()

    return new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(requestId)
        reject("AI timeout")
      }, timeoutMs)

      this.pending.set(requestId, { resolve, reject, timeout })

      const message: AiRequest = {
        type: "AI_REQUEST",
        requestId,
        action,
        userMessage,
        utility,
        context,
      }

      window.postMessage(message, window.location.origin)
    })
  }

  private handleMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return

    const message = event.data
    if (!message || typeof message !== 'object' || message.type !== "AI_RESPONSE") return
    const response = message as AiResponse


    const pending = this.pending.get(response.requestId)
    if (!pending) return

    clearTimeout(pending.timeout)
    this.pending.delete(response.requestId)

    if (response.success && response.data !== undefined) {
      pending.resolve(response.data)
    } else {
      pending.reject(response.error ?? "AI error")
    }
  }
}

export const aiBridge = new AiBridge()